const config = window.AbdelmegedTheme || {};
const logos = document.querySelectorAll('[data-logo-writing]');
const animationDuration = 2400;
const fillDuration = 280;
const svgNamespace = 'http://www.w3.org/2000/svg';

const reducedMotion = () => document.documentElement.dataset.reducedMotion === 'true';

const notifyLogo = (element, status) => {
  element.dispatchEvent(new CustomEvent(`abdelmeged:logo-animation-${status}`, {
    bubbles: true,
    detail: { element },
  }));
};

const canAnimateLogo = (image) => {
  if (config.features?.logoAnimation !== true || reducedMotion()) {
    return false;
  }

  try {
    const url = new URL(image.currentSrc || image.src, window.location.href);

    return url.origin === window.location.origin && url.pathname.toLowerCase().endsWith('.svg');
  } catch {
    return false;
  }
};

const buildAccessibleTitle = (svg, label, index) => {
  svg.removeAttribute('aria-label');

  if (!label) {
    svg.setAttribute('aria-hidden', 'true');
    return;
  }

  const title = document.createElementNS(svgNamespace, 'title');
  const titleId = `abdelmeged-logo-title-${index}`;

  title.id = titleId;
  title.textContent = label;
  svg.prepend(title);
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-labelledby', titleId);
};

const prepareSvg = (svg, image, index) => {
  svg.querySelectorAll('script, foreignObject').forEach((node) => node.remove());
  svg.removeAttribute('width');
  svg.removeAttribute('height');
  svg.removeAttribute('style');
  svg.setAttribute('focusable', 'false');
  svg.setAttribute('class', `${image.className} abdelmeged-logo-writing-svg is-preparing`.trim());
  Array.from(image.attributes)
    .filter((attribute) => attribute.name.startsWith('data-'))
    .forEach((attribute) => svg.setAttribute(attribute.name, attribute.value));
  buildAccessibleTitle(svg, image.getAttribute('alt') || '', index);

  return svg;
};

const finishAnimation = (svg, paths) => {
  paths.forEach((path) => {
    path.style.fill = 'currentColor';
    path.style.strokeDashoffset = '0';
  });

  svg.classList.remove('is-preparing');
  svg.classList.add('is-complete');
  notifyLogo(svg, 'complete');
};

const animatePaths = (svg) => {
  const paths = Array.from(svg.querySelectorAll('path'));

  if (paths.length === 0) {
    svg.classList.remove('is-preparing');
    svg.classList.add('is-complete');
    return;
  }

  const stagger = Math.max(70, Math.floor((animationDuration - 900) / paths.length));
  const pathDuration = Math.max(560, animationDuration - (stagger * paths.length));
  const totalDuration = (stagger * (paths.length - 1)) + pathDuration + fillDuration;

  paths.forEach((path, index) => {
    const length = Math.ceil(path.getTotalLength?.() || 120);
    const delay = stagger * index;

    path.style.fill = 'transparent';
    path.style.stroke = 'currentColor';
    path.style.strokeWidth = '0.5';
    path.style.strokeLinecap = 'round';
    path.style.strokeLinejoin = 'round';
    path.style.vectorEffect = 'non-scaling-stroke';
    path.style.strokeDasharray = String(length);
    path.style.strokeDashoffset = String(length);
    path.style.animation = `abdelmeged-logo-path ${pathDuration}ms var(--ease-standard) ${delay}ms forwards`;
  });

  window.setTimeout(() => finishAnimation(svg, paths), totalDuration);
};

const inlineAndAnimate = async (image, index) => {
  if (!canAnimateLogo(image)) {
    notifyLogo(image, 'skipped');
    return;
  }

  try {
    const response = await fetch(image.currentSrc || image.src, { credentials: 'same-origin' });

    if (!response.ok) {
      return;
    }

    const markup = await response.text();
    const parsed = new DOMParser().parseFromString(markup, 'image/svg+xml');
    const svg = parsed.documentElement;

    if (svg.nodeName.toLowerCase() !== 'svg') {
      notifyLogo(image, 'skipped');
      return;
    }

    image.replaceWith(prepareSvg(svg, image, index));
    window.requestAnimationFrame(() => animatePaths(svg));
  } catch {
    // Keep the original logo image if the SVG cannot be inlined.
    notifyLogo(image, 'skipped');
  }
};

logos.forEach((image, index) => {
  inlineAndAnimate(image, index);
});
